import { observable, action, computed } from "mobx";
import moment from "moment";
import Api from "../service/Api";
import BindDataToStore from "../utils/BindDataToStore";

const dateFormat = "MM/DD/YYYY";

class Product {
  @observable productId = null;

  @observable name = null;

  @observable creditCost = null;

  constructor(data) {
    this.productId = data.productId;
    this.name = data.name;
    this.creditCost = data.creditCost;
  }
}

class ProductDiscount {
  @observable productDiscountId = null;

  @observable productId = null;

  @observable creditCost = null;

  @observable start = null;

  @observable stop = null;

  constructor(data = {}) {
    this.init(data);
  }

  @action
  init(data = {}) {
    const getMomentDate = (date, forceDate) => {
      if (!date) {
        return forceDate ? moment() : null;
      }

      // Is start a string (coming from the backend) or is it a MomentJs object? (previously processed by this app)?
      return typeof date.isMomentObject !== "undefined" ? date : moment(date, dateFormat);
    };

    // note that 'start' must be always set. Only stop date can be null.
    const { start, stop, productDiscountId, creditCost, productId } = data;

    this.start = getMomentDate(start, true);
    this.stop = getMomentDate(stop);
    this.productDiscountId = productDiscountId;
    this.creditCost = creditCost;
    this.productId = productId;
  }
}

export default class ProviderProductDiscounts {
  @observable discounts = [];

  @observable products = [];

  @observable loading = true;

  @observable modalFormOpen = false;

  @observable discountFormData = new ProductDiscount();

  constructor(data = {}) {
    this.init(data);
  }

  @action
  init(data) {
    const { productDiscounts = [], products = [], ...rest } = data;

    for (let i = 0; i < productDiscounts.length; i += 1) {
      const productDiscount = productDiscounts[i];
      this.discounts.push(new ProductDiscount(productDiscount));
    }

    for (let i = 0; i < products.length; i += 1) {
      const product = products[i];
      this.products.push(new Product(product));
    }

    BindDataToStore(this, rest);
  }

  // Since a given product can have only one product discount, we'll return here
  // only those products that doesn't have a discount already.
  @computed get remainingProducts() {
    const remainingProducts = [];

    for (let i = 0; i < this.products.length; i += 1) {
      const product = this.products[i];

      let found = false;

      for (let j = 0; j < this.discounts.length; j += 1) {
        const discount = this.discounts[j];

        if (discount.productId === product.productId) {
          found = true;
          break;
        }
      }

      if (!found) {
        remainingProducts.push(product);
      }
    }

    return remainingProducts;
  }

  @action
  showModalForm(discount = {}) {
    this.discountFormData.init(discount);
    this.modalFormOpen = true;
  }

  @action
  closeModalForm() {
    this.modalFormOpen = false;
  }

  @action
  onInputChange(name, value) {
    if (["start", "stop"].indexOf(name) !== -1) {
      // make sure not to attempt to create a date object from a null value.
      this.discountFormData[name] = value ? moment(value) : null;
    } else {
      this.discountFormData[name] = value;
    }
  }

  @action
  save(providerId) {
    if (!this.validateDiscountFormData(this.discountFormData, this.products)) {
      return Promise.resolve();
    }

    const { productDiscountId, productId, creditCost, start, stop } = this.discountFormData;

    let url = `/provider/${providerId}/product-discounts`;

    // If there's a productDiscountId then we'll perform and 'update' action, since the record already exists
    // in the db.
    if (productDiscountId) {
      url = `${url}/${productDiscountId}`;
    }

    return Api.post(url, {
      productId,
      creditCost,
      start: start.format(dateFormat),
      stop: stop ? stop.format(dateFormat) : null,
    })
      .then(data => {
        // Did we just create a new record? If so let's append it to the list of discounts.
        if (!productDiscountId) {
          // create a new discount from the form data.
          this.discounts.push(new ProductDiscount(data));
        } else {
          // We executed an update. Let's find the matching record and update it accordingly.
          let found = false;

          for (let i = 0; i < this.discounts.length; i += 1) {
            const discount = this.discounts[i];

            if (discount.productDiscountId === productDiscountId) {
              discount.init(data);
              found = true;
              break;
            }
          }

          if (!found) {
            throw new Error("Could not update UI");
          }
        }
        // finally clear the form.
        this.discountFormData.init({});
        this.closeModalForm();
      })
      .catch(err => {
        alert("An error ocurred.");
        throw err;
      });
  }

  @action
  delete(providerId, discount) {
    if (!window.confirm("Are you sure you want to delete this discount?")) {
      return;
    }

    const url = `/provider/${providerId}/product-discounts/${discount.productDiscountId}`;
    this.loading = true;

    Api.delete(url)
      .then(() => {
        // remove the discount from the list.

        for (let i = 0; i < this.discounts.length; i += 1) {
          const discountInLoop = this.discounts[i];

          if (discount.productDiscountId === discountInLoop.productDiscountId) {
            this.discounts.splice(i, 1);
            break;
          }
        }

        this.loading = false;
      })
      .catch(err => {
        alert(`An error has occurred: ${err}`);
        throw err;
      });
  }

  validateDiscountFormData(discountFormData, products) {
    const { productDiscountId, productId, creditCost, start, stop } = discountFormData;

    // A product must be selected.
    if (!productId) {
      alert("Please select a product");

      return false;
    }

    // Validate that the chosen product is not already taken by an existing discount (that is not self)
    // Note that there should be only one discount per product.
    for (let i = 0; i < this.discounts.length; i += 1) {
      const discount = this.discounts[i];

      // Is the chosen product already taken? Is it taken by another discount? If yes to all, then bail.
      if (productId === discount.productId && productDiscountId !== discount.productDiscountId) {
        alert(
          "The selected product has a discount already. Please edit the existing discount or choose a different product",
        );

        return false;
      }
    }

    // validate there's a cost and that it's an integer.
    if (!/^\d+$/.test(creditCost)) {
      alert("Please enter a valid credit cost");

      return false;
    }

    // make sure the cost is lower or equal than the current product cost.
    const product = products.find(productInLoop => productInLoop.productId === productId);

    if (!product) {
      const errorMessage = `Expected to find product matching product id ${productId}`;
      alert(errorMessage);

      throw new Error(errorMessage);
    }

    if (creditCost > product.creditCost) {
      alert(`Credit cost cannot be higher than the actual product credit cost (${product.creditCost})`);

      return false;
    }

    // validate start date.
    if (!start.isValid()) {
      alert("Invalid value for start date");

      return false;
    }

    // validate stop date
    if (stop !== null && !stop.isValid()) {
      alert("Invalid value for stop date");

      return false;
    }

    return true;
  }
}

export { Product, ProductDiscount };
